import React, {
  ClassAttributes,
  DetailedHTMLProps,
  FC,
  InputHTMLAttributes,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import classNames from 'classnames';

import './Input.scss';

import {
  AnalyticsContext,
  handleAnalyticsEvent,
  TAnalyticsEventData,
} from '@dtci/dash/analytics';

import { InputWidth, TestMetadata } from '../types';
import Typography from '../Typography';

const ARIA_LABEL_CONST = 'aria-label',
  DATA_TEST_ID_CONST = 'data-testid';

export interface InputProps
  extends DetailedHTMLProps<
    InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  > {
  /** value of the input field */
  value?: number | string;
  /** Boolean to determine if field is required */
  required?: boolean;
  /** If label is not visible, use aria-label for screen readers */
  labelText?: string;
  /** ref form inner input **/
  inputRef?: ClassAttributes<HTMLInputElement>['ref'];
  /** Width of the Input */
  width?: InputWidth;
  // For use with nested components, ie. inputProps
  DATA_TEST_ID_CONST?: string;
  testMetadata?: TestMetadata;
  /** Error State */
  isError?: boolean;
  /** Disabled State */
  isDisabled?: boolean;
  /** Border Flush */
  flush?: 'left' | 'right' | 'flush';
  /** Indicate if input should have a border */
  bordered?: boolean;
  /** Max length of characters required */
  maxLength?: number;
  /** show a character count */
  charCount?: boolean;
  /** Additional event information to pass to analytics tracking */
  analyticsEventData?: TAnalyticsEventData;
}
/** Input components are useful for editing text in forms */
const Input: FC<InputProps> = ({
  className,
  labelText,
  inputRef,
  type = 'text',
  required,
  width = 'small',
  testMetadata,
  isError = false,
  flush,
  bordered = true,
  isDisabled,
  maxLength = -1,
  charCount = false,
  analyticsEventData,
  ...otherProps
}) => {
  const inputClassNames = useMemo(
    () =>
      classNames(
        'sb-input',
        {
          'sb-input_error': isError,
          'sb-input__is-required': required,
          'sb-input__is-disabled': isDisabled,
          'sb-input__size-small': width === 'small',
          'sb-input__size-medium': width === 'medium',
          'sb-input__size-large': width === 'large',
          'sb-input__size-auto': width === 'auto',
          'sb-input__flush-right': flush === 'right',
          'sb-input__flush': flush === 'flush',
          'sb-input__flush-left': flush === 'left',
          'sb-input__borderless': !bordered,
        },
        className,
      ),
    [className, required, width, isError, flush, bordered, isDisabled],
  );

  const analyticsContext = useContext(AnalyticsContext);
  const { onFocus, onBlur, onKeyDown, onChange, value } = otherProps;

  const [chars, setChars] = useState(value ? value?.toString().length : 0);

  const handleOnFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>): void => {
      if (onFocus) onFocus?.(e);

      if (analyticsEventData) {
        onFocus?.(e);
        handleAnalyticsEvent({
          analyticsContext,
          analyticsEventData,
          componentType: 'Input',
          actionName: 'focus',
          label:
            otherProps?.[ARIA_LABEL_CONST] ||
            otherProps?.name ||
            labelText ||
            className ||
            testMetadata?.[DATA_TEST_ID_CONST] ||
            '',
          value: [{ text: e.target.value }],
        });
      }
    },
    [
      analyticsContext,
      analyticsEventData,
      className,
      labelText,
      onFocus,
      otherProps,
      testMetadata,
    ],
  );

  const handleOnBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>): void => {
      if (onBlur) onBlur?.(e);

      if (analyticsEventData) {
        handleAnalyticsEvent({
          analyticsContext,
          analyticsEventData,
          componentType: 'Input',
          actionName: 'blur',
          label:
            otherProps?.[ARIA_LABEL_CONST] ||
            otherProps?.name ||
            labelText ||
            className ||
            testMetadata?.[DATA_TEST_ID_CONST] ||
            '',
          value: [{ text: e.target.value }],
        });
      }
    },
    [
      analyticsContext,
      analyticsEventData,
      className,
      labelText,
      onBlur,
      otherProps,
      testMetadata,
    ],
  );

  const handleOnKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>): void => {
      if (onKeyDown) onKeyDown?.(e);

      if (analyticsEventData) {
        if (e.key !== 'Enter') return;

        handleAnalyticsEvent({
          analyticsContext,
          analyticsEventData,
          componentType: 'Input',
          actionName: 'enter',
          label:
            otherProps?.[ARIA_LABEL_CONST] ||
            otherProps?.name ||
            labelText ||
            className ||
            testMetadata?.[DATA_TEST_ID_CONST] ||
            '',
          value: [{ text: e.currentTarget.value }],
        });
      }
    },
    [
      analyticsContext,
      analyticsEventData,
      className,
      labelText,
      otherProps,
      testMetadata,
      onKeyDown,
    ],
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    charCount && setChars(e.target.value.length);
    onChange?.(e);
  };

  return (
    <>
      <input
        {...otherProps}
        onFocus={handleOnFocus}
        onBlur={handleOnBlur}
        onChange={handleChange}
        onKeyDown={handleOnKeyDown}
        className={inputClassNames}
        required={required}
        aria-required={required}
        aria-label={labelText}
        ref={inputRef}
        type={type}
        maxLength={maxLength}
        {...testMetadata}
      />
      {charCount && (
        <Typography
          variant="T10"
          className="sb-textarea__characters"
          testMetadata={{ [DATA_TEST_ID_CONST]: 'textAreaCharCount' }}
        >{`${chars} ${maxLength ? ' / ' + maxLength : ''}`}</Typography>
      )}
    </>
  );
};

export default Input;
